<template>
	<div class="brand-insight-tab">
		<HistorySidebar
			:current-conversation-id="currentConversationId"
			@select-chat="handleSelectChat"
			@new-topic="handleNewTopic"
		/>

		<div class="brand-insight-tab__main">
			<div class="main-content">
				<div class="greeting">
					<img src="@/assets/images/default_avatar.jpg" alt="greeting" class="greeting-icon" />
					<h2 class="greeting-text">你好，今天有什么可以帮到你?</h2>
				</div>

				<div class="suggestions">
					<span
						v-for="(suggestion, index) in suggestions"
						:key="index"
						class="suggestion-item"
						@click="handleSuggestionClick(suggestion)"
					>
						{{ suggestion }}
					</span>
				</div>

				<div class="search-container">
					<textarea
						v-model="searchText"
						class="search-input"
						placeholder="请输入你想了解的问题"
						rows="4"
						@keydown.ctrl.enter="handleSearch"
						@keydown.meta.enter="handleSearch"
					></textarea>
					<button type="button" class="send-btn" @click="handleSearch">
						<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M22 2L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
					</button>
				</div>

				<div class="date-progress-wrapper">
					<BaseDatePicker
						v-model="dateRange"
						type="date"
						range
						placeholder="选择日期范围"
					></BaseDatePicker>
					<CircularProgress :percentage="progressPercentage" />

					<button type="button" class="option-btn active" disabled>
						运营数据
					</button>

					<div class="options-percentage-container">
						<div class="options-group">
							<button
								type="button"
								class="option-btn"
								:class="{ active: include_intent_detail }"
								@click="include_intent_detail = !include_intent_detail"
							>
								客户需求挖掘与引导
							</button>
							<button
								type="button"
								class="option-btn"
								:class="{ active: include_satisfaction_detail }"
								@click="include_satisfaction_detail = !include_satisfaction_detail"
							>
								客户满意度
							</button>
							<button
								type="button"
								class="option-btn"
								:class="{ active: include_quality_detail }"
								@click="include_quality_detail = !include_quality_detail"
							>
								客户回复质量
							</button>
						</div>

						<div class="percentage-selector">
							<select v-model="selectedPercentage" class="percentage-select">
								<option
									v-for="percent in percentageOptions"
									:key="percent"
									:value="percent"
									:detail_sample_rate="percent"
								>
									{{ percent }}%
								</option>
							</select>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import HistorySidebar from '@/components/HistorySidebar.vue';
import BaseDatePicker from '@/components/BaseDatePicker.vue';
import CircularProgress from '@/components/CircularProgress.vue';
import { DateFormatter } from '@/utils/filter';

export default {
	name: 'BrandMarketInsightTab',
	components: {
		HistorySidebar,
		BaseDatePicker,
		CircularProgress
	},
	data() {
		return {
			searchText: '',
			currentConversationId: null,
			dateRange: null,
			progressPercentage: 0,
			include_intent_detail: false,
			include_satisfaction_detail: false,
			include_quality_detail: false,
			selectedPercentage: 5,
			percentageOptions: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100],
			suggestions: [
				'目前私域整体客户关系如何?',
				'改善当前客服质量最关键的动作是什么?',
				'除了客服个人能力，我们的内部流程有没有给客户带来麻烦?有什么证据?',
				'上周损失了多少个引导客户成交的机会?',
				'客服专业度如何?',
				'客服态度如何?',
				'客服积极性如何?',
				'跟上个月比我们现在客服专业度提升了多少？'
			]
		};
	},
	watch: {
		dateRange(newVal) {
			if (newVal && Array.isArray(newVal) && newVal.length === 2) {
				this.fetchTokenLength(newVal);
			} else {
				this.progressPercentage = 0;
			}
		},
		include_intent_detail() {
			if (this.dateRange && Array.isArray(this.dateRange) && this.dateRange.length === 2) {
				this.fetchTokenLength(this.dateRange);
			}
		},
		include_satisfaction_detail() {
			if (this.dateRange && Array.isArray(this.dateRange) && this.dateRange.length === 2) {
				this.fetchTokenLength(this.dateRange);
			}
		},
		include_quality_detail() {
			if (this.dateRange && Array.isArray(this.dateRange) && this.dateRange.length === 2) {
				this.fetchTokenLength(this.dateRange);
			}
		},
		selectedPercentage() {
			if (this.dateRange && Array.isArray(this.dateRange) && this.dateRange.length === 2) {
				this.fetchTokenLength(this.dateRange);
			}
		}
	},
	methods: {
		handleSearch() {
			if (!this.searchText.trim()) {
				return;
			}
			if (!this.dateRange || !Array.isArray(this.dateRange) || this.dateRange.length !== 2) {
				this.$message.warning('请先选择日期范围');
				return;
			}
			this.goToChat(this.searchText);
			this.searchText = '';
		},
		handleSuggestionClick(suggestion) {
			if (!this.dateRange || !Array.isArray(this.dateRange) || this.dateRange.length !== 2) {
				this.$message.warning('请先选择日期范围');
				return;
			}
			this.goToChat(suggestion);
		},
		goToChat(message, conversationId = null) {
			const dateRangeStr = this.dateRange && Array.isArray(this.dateRange) && this.dateRange.length === 2
				? `${DateFormatter.format(this.dateRange[0], 'Y-M-D')},${DateFormatter.format(this.dateRange[1], 'Y-M-D')}`
				: '';

			const query = {
				message,
				dateRange: dateRangeStr,
				progress: this.progressPercentage,
				include_intent_detail: this.include_intent_detail ? 'true' : 'false',
				include_satisfaction_detail: this.include_satisfaction_detail ? 'true' : 'false',
				include_quality_detail: this.include_quality_detail ? 'true' : 'false',
				detail_sample_rate: this.selectedPercentage / 100
			};

			if (conversationId) {
				query.conversationId = conversationId;
			}

			this.$router.push({
				path: '/chat',
				query
			});
		},
		handleSelectChat(chat) {
			this.currentConversationId = String(chat.id);
			if (chat.data_start_date && chat.data_end_date) {
				this.dateRange = [
					new Date(chat.data_start_date),
					new Date(chat.data_end_date)
				];
				if (chat.token_length && chat.token_limit) {
					this.progressPercentage = Math.min(
						parseFloat(((chat.token_length / chat.token_limit) * 100).toFixed(2)),
						100
					);
				}
			}

			if (chat.include_intent_detail !== undefined) {
				this.include_intent_detail = chat.include_intent_detail;
			}
			if (chat.include_satisfaction_detail !== undefined) {
				this.include_satisfaction_detail = chat.include_satisfaction_detail;
			}
			if (chat.include_quality_detail !== undefined) {
				this.include_quality_detail = chat.include_quality_detail;
			}

			if (chat.detail_sample_rate !== undefined) {
				const rate = parseFloat(chat.detail_sample_rate);
				this.selectedPercentage = rate > 1 ? rate : Math.round(rate * 100);
			}

			this.goToChat(chat.title, chat.id);
		},
		handleNewTopic() {
			this.currentConversationId = null;
		},
		async fetchTokenLength(dateRange) {
			try {
				const [startDate, endDate] = dateRange;
				const dataStartDate = DateFormatter.format(startDate, 'Y-M-D');
				const dataEndDate = DateFormatter.format(endDate, 'Y-M-D');

				if (!dataStartDate || !dataEndDate) {
					return;
				}

				const detailSampleRate = (this.selectedPercentage / 100).toFixed(2);

				const res = await this.$get('/manager-conversation/system-token-length', {
					data_start_date: dataStartDate,
					data_end_date: dataEndDate,
					detail_sample_rate: detailSampleRate,
					include_intent_detail: this.include_intent_detail,
					include_quality_detail: this.include_quality_detail,
					include_satisfaction_detail: this.include_satisfaction_detail
				});

				if (res.code === 200 && res.data) {
					const { token_length, max_context_length } = res.data;
					if (max_context_length > 0 && token_length > 0) {
						const percentage = (token_length / max_context_length) * 100;
						this.progressPercentage = Math.min(parseFloat(percentage.toFixed(2)), 100);
					} else {
						this.progressPercentage = 0;
					}
				}
			} catch (error) {
				console.error('获取 token 长度失败:', error);
				this.progressPercentage = 0;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.brand-insight-tab {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.brand-insight-tab__main {
	flex: 1;
	min-height: 0;
	overflow: auto;
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
	padding-left: var(--home-layout-sidebar-inset, 420px);
	padding-right: calc(30px + var(--home-layout-right-rail, 300px));
}

.main-content {
	flex: 1;
	min-height: 0;
	width: 100%;
	max-width: var(--home-layout-main-max, 900px);
	margin: 0 auto;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: flex-start;
	padding: var(--home-layout-greeting-top, 90px) var(--home-layout-main-pad-x, 24px) 40px;
}

.greeting {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	margin-bottom: 50px;
	align-self: center;
	width: auto;
	max-width: 100%;
}

.greeting-icon {
	width: 28px;
	height: 28px;
	object-fit: contain;
	display: block;
	border-radius: 50%;
}

.greeting-text {
	font-size: 21px;
	color: #333;
}

.suggestions {
	width: 100%;
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	justify-content: flex-start;
	margin: 30px 0 60px;
}

.suggestion-item {
	display: inline-block;
	padding: 6px 10px;
	background: #e0e0e0;
	border-radius: 4px;
	text-align: left;
	font-size: 12px;
	cursor: pointer;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
	width: fit-content;
	max-width: 100%;
}

.suggestion-item:hover {
	border-color: #fdc408;
	transform: translateX(2px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-container {
	width: 100%;
	position: relative;
	margin-bottom: 20px;
}

.search-input {
	width: 100%;
	padding: 16px 60px 16px 20px;
	box-sizing: border-box;
	border-radius: 6px;
	background: #fff;
	outline: none;
	transition: all 0.3s ease;
	box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.2);
	resize: none;
	line-height: 1.5;
}

.search-input:focus {
	border-color: #fdc408;
	box-shadow: 0 2px 12px rgba(253, 196, 8, 0.15);
}

.send-btn {
	position: absolute;
	right: 15px;
	bottom: 10px;
	width: 26px;
	height: 26px;
	border: none;
	background: #ffd700;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #fff;
	z-index: 1;
}

.date-progress-wrapper {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 16px;

	.mx-datepicker-range {
		width: 220px;
	}

	:deep(.mx-input) {
		border: none;
		box-shadow: none;
		background: #ebebec;
		border-radius: 6px;
		padding: 10px 10px;
		font-size: 12px;
	}
}

.options-percentage-container {
	display: flex;
	align-items: center;
	gap: 12px;
	background: #cbcbcb;
	border-radius: 6px;
	padding: 6px 10px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.options-group {
	display: flex;
	gap: 8px;
	align-items: center;
}

.option-btn {
	padding: 3px 8px;
	border: 1px solid #ddd;
	background: #fff;
	border-radius: 8px;
	font-size: 11px;
	cursor: pointer;
	transition: all 0.3s ease;
	white-space: nowrap;

	&.active {
		background: #fdc408;
		border-color: #fdc408;
		color: #333;
		font-weight: 500;
	}

	&:disabled {
		background: #fdc408;
		border-color: #fdc408;
		color: #333;
		font-weight: 500;
		cursor: default;
		opacity: 1;
	}
}

.percentage-selector {
	position: relative;
}

.percentage-select {
	padding: 4px 20px 4px 8px;
	border: 1px solid #ddd;
	background: #fff;
	border-radius: 8px;
	font-size: 11px;
	cursor: pointer;
	appearance: none;
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
	background-repeat: no-repeat;
	background-position: right 8px center;
	background-size: 12px;

	&:hover {
		border-color: #fdc408;
	}

	&:focus {
		outline: none;
		border-color: #fdc408;
		box-shadow: 0 0 0 2px rgba(253, 196, 8, 0.2);
	}
}
</style>
